import React, { useEffect, useState, useMemo } from "react";
import { Modal, Button, Form, Spinner, Badge } from "react-bootstrap";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { AlbumDTO } from "../../../utils/apiTypes";
import { createAlbum, updateAlbum, deleteAlbum, uploadAlbumMotionCover, Spotip17anFunctions, API_POST_CREATE_OR_GET_FOLDER, CustomApi, normalizeFileUrl } from "../../../utils/api";
import { ArtistDTO, Song } from "../../view/spotipitan/types";
import useUserStore from "../../../store/UserState";
import notFoundImage from "../../../images/not-found.png";
import ConfirmationModal from "../confirmation-modal/confirmation-modal";
import "./album-edit-modal.scss";

export interface AlbumEditModalProps {
    show: boolean;
    album?: AlbumDTO | ArtistDTO | null;
    onClose: () => void;
    onSave: (album: AlbumDTO) => void;
    onDelete?: (albumId: string) => void;
}

const AlbumEditModal: React.FC<AlbumEditModalProps> = ({
    show,
    album,
    onClose,
    onSave,
    onDelete
}) => {
    const isEditMode = Boolean(album && album.id);
    const { spotip17anUser } = useUserStore(state => state.state);
    const navigate = useNavigate();

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [releaseDate, setReleaseDate] = useState("");
    const [coverUrl, setCoverUrl] = useState("");
    const [coverFile, setCoverFile] = useState<File | null>(null);
    const [coverPreview, setCoverPreview] = useState("");
    const [motionFile, setMotionFile] = useState<File | null>(null);
    const [motionPreviewUrl, setMotionPreviewUrl] = useState<string>("");

    const [artistSongs, setArtistSongs] = useState<Song[]>([]);
    const [albumSongs, setAlbumSongs] = useState<Song[]>([]);
    const [loadingSongs, setLoadingSongs] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [selectedSongToAdd, setSelectedSongToAdd] = useState<string>("");

    // Initialize or reset form when modal opens or album changes
    useEffect(() => {
        if (!show) {
            setCoverFile(null);
            setCoverPreview("");
            setMotionFile(null);
            setMotionPreviewUrl("");
            return;
        }

        if (album) {
            setTitle(album.title || album.username || album.name || "");
            setDescription(album.description || "");
            const rDate = (album as any).release_date || (album as any).releaseDate;
            if (rDate) {
                const dateStr = typeof rDate === "string" ? rDate.substring(0, 10) : new Date(rDate).toISOString().substring(0, 10);
                setReleaseDate(dateStr);
            } else {
                setReleaseDate("");
            }
            const initialCover = normalizeFileUrl(album.cover_url || album.poster_url || "");
            setCoverUrl(initialCover);
            setCoverPreview(initialCover);
            const initialMotion = normalizeFileUrl((album as any).motion_cover_url || (album as any).motionCoverUrl || "");
            setMotionPreviewUrl(initialMotion);
            setMotionFile(null);
            setAlbumSongs(album.songs ? [...album.songs] : []);
        } else {
            setTitle("");
            setDescription("");
            setReleaseDate(new Date().toISOString().substring(0, 10));
            setCoverUrl("");
            setCoverPreview("");
            setMotionPreviewUrl("");
            setMotionFile(null);
            setAlbumSongs([]);
        }

        // Fetch artist songs to allow selecting which songs to add
        setLoadingSongs(true);
        Spotip17anFunctions.use('getSpotip17anData').call().then((data) => {
            if (data?.songs && Array.isArray(data.songs)) {
                setArtistSongs(data.songs);
            }
        }).catch((err) => {
            console.error("Error loading artist songs for album modal:", err);
        }).finally(() => {
            setLoadingSongs(false);
        });
    }, [show, album]);

    // Handle selecting local image cover
    const handleSelectCoverFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setCoverFile(file);
        setCoverPreview(URL.createObjectURL(file));
    };

    const handleRemoveCoverFile = () => {
        setCoverFile(null);
        const input = document.getElementById("album-cover-input") as HTMLInputElement;
        if (input) input.value = "";
        const original = normalizeFileUrl(album?.cover_url || album?.poster_url || "");
        setCoverPreview(original);
        setCoverUrl(original);
    };

    // Handle selecting local motion video (MP4)
    const handleSelectMotionFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.includes("mp4") && !file.name.toLowerCase().endsWith(".mp4")) {
            toast.error("El archivo debe ser un video en formato MP4");
            return;
        }
        if (file.size > 30 * 1024 * 1024) {
            toast.error("El video no puede superar los 30MB");
            return;
        }
        setMotionFile(file);
        setMotionPreviewUrl(URL.createObjectURL(file));
        toast.success(`Video seleccionado: ${file.name}`);
    };

    const handleRemoveMotionFile = () => {
        setMotionFile(null);
        setMotionPreviewUrl("");
        const input = document.getElementById("album-motion-input") as HTMLInputElement;
        if (input) input.value = "";
    };

    // Songs available to add that are not already in the album
    const availableSongs = useMemo(() => {
        const currentIds = new Set(albumSongs.map(s => String(s.id)));
        return artistSongs.filter(s => !currentIds.has(String(s.id)));
    }, [artistSongs, albumSongs]);

    // Add selected song to album tracks
    const handleAddSong = (explicitSongId?: string) => {
        const idToAdd = explicitSongId || selectedSongToAdd;
        if (!idToAdd) return;
        const songToAdd = artistSongs.find(s => String(s.id) === String(idToAdd));
        if (songToAdd) {
            setAlbumSongs(prev => {
                if (prev.some(s => String(s.id) === String(songToAdd.id))) return prev;
                return [...prev, songToAdd];
            });
            // If no cover is set yet, propose the first added song's poster
            if (!coverPreview && !coverUrl && songToAdd.poster_url) {
                setCoverUrl(songToAdd.poster_url);
                setCoverPreview(songToAdd.poster_url);
            }
            setSelectedSongToAdd("");
        }
    };

    // Remove song from album
    const handleRemoveSong = (indexToRemove: number) => {
        setAlbumSongs(prev => prev.filter((_, idx) => idx !== indexToRemove));
    };

    // Reorder tracks
    const handleMoveTrack = (index: number, direction: 'up' | 'down') => {
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= albumSongs.length) return;

        setAlbumSongs(prev => {
            const updated = [...prev];
            const temp = updated[index];
            updated[index] = updated[targetIndex];
            updated[targetIndex] = temp;
            return updated;
        });
    };

    // Upload cover image file to server if selected
    const uploadCoverImageIfNeeded = async (): Promise<string> => {
        if (!coverFile) return coverUrl;
        if (!spotip17anUser?.token) return coverUrl;

        try {
            const folderRes = await CustomApi.post(
                API_POST_CREATE_OR_GET_FOLDER,
                { folderName: "spotip17an albums💽" },
                { token: spotip17anUser.token }
            );

            return await new Promise<string>((resolve, reject) => {
                CustomApi.uploadFile(
                    coverFile,
                    coverFile.name,
                    spotip17anUser.token,
                    folderRes.id,
                    (event) => {
                        try {
                            const res = JSON.parse((event.target as any).response);
                            resolve(res.file.url);
                        } catch (err) {
                            reject(err);
                        }
                    },
                    (err) => reject(err)
                );
            });
        } catch (err) {
            console.error("Error uploading album cover:", err);
            return coverUrl;
        }
    };

    // Save album
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim()) {
            toast.error("El título del álbum es obligatorio");
            return;
        }

        setSaving(true);
        try {
            const rawCoverUrl = await uploadCoverImageIfNeeded();
            const finalCoverUrl = normalizeFileUrl(rawCoverUrl);
            let finalSongs = [...albumSongs];
            if (selectedSongToAdd) {
                const pending = artistSongs.find(s => String(s.id) === String(selectedSongToAdd));
                if (pending && !finalSongs.some(s => String(s.id) === String(pending.id))) {
                    finalSongs.push(pending);
                }
            }
            const songIds = finalSongs.map(s => String(s.id));

            let resultAlbum: AlbumDTO;

            if (isEditMode && album) {
                resultAlbum = await updateAlbum({
                    albumId: String(album.id),
                    title: title.trim(),
                    description: description.trim() ? description.trim() : null,
                    cover_url: finalCoverUrl || undefined,
                    releaseDate: releaseDate ? releaseDate : null,
                    songIds: songIds,
                });
            } else {
                resultAlbum = await createAlbum({
                    title: title.trim(),
                    description: description.trim() || undefined,
                    cover_url: finalCoverUrl || undefined,
                    releaseDate: releaseDate || undefined,
                    songIds: songIds.length > 0 ? songIds : undefined,
                });
            }

            // Upload motion video if selected
            if (motionFile && resultAlbum.id) {
                try {
                    await uploadAlbumMotionCover(resultAlbum.id, motionFile);
                    toast.success("Portada animada subida correctamente");
                } catch (mErr: any) {
                    console.error("Error uploading motion cover:", mErr);
                    toast.error(mErr?.message || "No se pudo subir la portada animada");
                }
            }

            toast.success(isEditMode ? "Álbum actualizado correctamente" : "Álbum creado correctamente");
            onSave(resultAlbum);
            onClose();
        } catch (err: any) {
            console.error("Error saving album:", err);
            toast.error(err?.message || "Error al guardar el álbum");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!album?.id) return;
        setDeleting(true);
        try {
            await deleteAlbum(String(album.id));
            toast.success("Álbum eliminado correctamente");
            setShowConfirmDelete(false);
            onClose();
            if (onDelete) {
                onDelete(String(album.id));
            } else {
                navigate("/my-library");
            }
        } catch (err: any) {
            console.error("Error deleting album:", err);
            toast.error(err?.message || "Error al eliminar el álbum");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <Modal
                show={show}
                onHide={saving ? undefined : onClose}
                size="lg"
                centered
                className="custom-modal album-edit-modal"
            >
                <Modal.Header closeButton={!saving}>
                    <Modal.Title>
                        <i className={`fa-solid ${isEditMode ? "fa-pen-to-square" : "fa-compact-disc"} me-2`}></i>
                        {isEditMode ? "Editar álbum" : "Crear nuevo álbum"}
                    </Modal.Title>
                </Modal.Header>

                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        {/* Seccion 1: Portada + Datos Principales */}
                        <div className="d-flex flex-column flex-md-row gap-3 mb-3">
                            <div className="d-flex flex-column align-items-center">
                                <div className="album-edit-modal__cover-preview mb-2">
                                    <img
                                        src={normalizeFileUrl(coverPreview) || notFoundImage}
                                        alt="Portada del álbum"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = notFoundImage;
                                        }}
                                    />
                                </div>
                                <input
                                    type="file"
                                    id="album-cover-input"
                                    style={{ display: "none" }}
                                    accept="image/*"
                                    onChange={handleSelectCoverFile}
                                />
                                <div className="d-flex flex-column gap-1 w-100">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => document.getElementById("album-cover-input")?.click()}
                                        disabled={saving}
                                    >
                                        <i className="fa-solid fa-image me-1"></i>
                                        {coverFile ? "Cambiar portada" : "Subir portada"}
                                    </Button>
                                    {coverFile && (
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={handleRemoveCoverFile}
                                            disabled={saving}
                                        >
                                            <i className="fa-solid fa-xmark me-1"></i>
                                            Quitar selección
                                        </Button>
                                    )}
                                </div>

                                <div className="mt-2 w-100">
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        className="text-center"
                                        placeholder="O pega URL de imagen"
                                        value={coverUrl}
                                        onChange={(e) => {
                                            setCoverUrl(e.target.value);
                                            setCoverPreview(e.target.value);
                                        }}
                                        disabled={saving}
                                    />
                                </div>
                            </div>

                            <div className="flex-grow-1">
                                <Form.Group className="mb-2">
                                    <Form.Label>Título del Álbum <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        required
                                        placeholder="Nombre del álbum"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        disabled={saving}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-2">
                                    <Form.Label>Fecha de lanzamiento</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={releaseDate}
                                        onChange={(e) => setReleaseDate(e.target.value)}
                                        disabled={saving}
                                    />
                                    <div className="album-edit-modal__hint mt-1">
                                        Fecha oficial de publicación del álbum.
                                    </div>
                                </Form.Group>

                                <Form.Group className="mb-2">
                                    <Form.Label>Descripción</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        placeholder="Breve reseña o información sobre el álbum..."
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        disabled={saving}
                                    />
                                </Form.Group>
                            </div>
                        </div>

                        {/* Seccion 2: Portada Animada / Motion Artwork (MP4) */}
                        <div className="album-edit-modal__section-box">
                            <div className="album-edit-modal__section-title">
                                <i className="fa-solid fa-film"></i>
                                Portada animada (Motion Artwork MP4)
                                {motionFile && (
                                    <Badge bg="info" className="ms-auto">
                                        Nuevo video seleccionado
                                    </Badge>
                                )}
                            </div>

                            <div className="d-flex align-items-center gap-3">
                                {motionPreviewUrl && (
                                    <div className="album-edit-modal__motion-preview">
                                        <video
                                            src={motionPreviewUrl}
                                            autoPlay
                                            loop
                                            muted
                                            playsInline
                                        />
                                    </div>
                                )}

                                <div className="flex-grow-1">
                                    <div className="album-edit-modal__hint mb-2">
                                        Video en bucle continuo para la cabecera del álbum (formato MP4 cuadrado 1:1, máx. 30MB).
                                    </div>

                                    <input
                                        type="file"
                                        id="album-motion-input"
                                        style={{ display: "none" }}
                                        accept="video/mp4"
                                        onChange={handleSelectMotionFile}
                                    />
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => document.getElementById("album-motion-input")?.click()}
                                            disabled={saving}
                                        >
                                            <i className="fa-solid fa-video me-1"></i>
                                            {motionFile || motionPreviewUrl ? "Cambiar portada animada" : "Subir video MP4"}
                                        </Button>
                                        {motionPreviewUrl && (
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={handleRemoveMotionFile}
                                                disabled={saving}
                                            >
                                                <i className="fa-solid fa-xmark me-1"></i>
                                                Quitar
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Seccion 3: Pistas del Álbum */}
                        <div className="album-edit-modal__section-box">
                            <div className="album-edit-modal__section-title">
                                <i className="fa-solid fa-list-ol"></i>
                                Pistas del Álbum ({albumSongs.length})
                                <span className="album-edit-modal__hint ms-auto">
                                    Reordena con las flechas
                                </span>
                            </div>

                            {/* Selector para añadir pistas */}
                            <div className="d-flex gap-2 mb-3">
                                <Form.Select
                                    value={selectedSongToAdd}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val) {
                                            handleAddSong(val);
                                        } else {
                                            setSelectedSongToAdd("");
                                        }
                                    }}
                                    disabled={loadingSongs || availableSongs.length === 0 || saving}
                                >
                                    <option value="">
                                        {loadingSongs
                                            ? "Cargando canciones..."
                                            : availableSongs.length === 0
                                                ? "No hay más canciones publicadas para añadir"
                                                : "Seleccionar canción para añadir al álbum..."}
                                    </option>
                                    {availableSongs.map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.name}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Button
                                    variant="primary"
                                    onClick={() => handleAddSong()}
                                    disabled={!selectedSongToAdd || saving}
                                    className="text-nowrap"
                                >
                                    <i className="fa-solid fa-plus me-1"></i> Añadir pista
                                </Button>
                            </div>

                            {/* Lista ordenada de pistas */}
                            <div className="album-edit-modal__songs-list">
                                {albumSongs.length === 0 ? (
                                    <div className="text-center text-muted py-3 small">
                                        No has añadido pistas aún. Añade canciones publicadas usando el selector superior.
                                    </div>
                                ) : (
                                    albumSongs.map((song, index) => (
                                        <div key={song.id + "-" + index} className="album-edit-modal__song-item">
                                            <span className="album-edit-modal__song-track">
                                                #{index + 1}
                                            </span>
                                            <img
                                                src={normalizeFileUrl(song.poster_url) || notFoundImage}
                                                alt={song.name}
                                                className="album-edit-modal__song-img"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).src = notFoundImage;
                                                }}
                                            />
                                            <div className="album-edit-modal__song-info">
                                                <div className="song-title">{song.name}</div>
                                            </div>
                                            <div className="album-edit-modal__song-actions">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary"
                                                    disabled={index === 0 || saving}
                                                    onClick={() => handleMoveTrack(index, 'up')}
                                                    title="Mover arriba"
                                                >
                                                    <i className="fa-solid fa-chevron-up"></i>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary"
                                                    disabled={index === albumSongs.length - 1 || saving}
                                                    onClick={() => handleMoveTrack(index, 'down')}
                                                    title="Mover abajo"
                                                >
                                                    <i className="fa-solid fa-chevron-down"></i>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger ms-1"
                                                    onClick={() => handleRemoveSong(index)}
                                                    disabled={saving}
                                                    title="Quitar pista del álbum"
                                                >
                                                    <i className="fa-solid fa-xmark"></i>
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {saving && (
                            <div className="text-center text-muted small mt-2">
                                <Spinner animation="border" size="sm" className="me-2" />
                                {isEditMode ? "Guardando cambios del álbum..." : "Creando álbum..."}
                            </div>
                        )}
                    </Modal.Body>

                    <Modal.Footer>
                        {isEditMode ? (
                            <Button
                                variant="outline-danger"
                                className="album-edit-modal__danger-btn"
                                onClick={() => setShowConfirmDelete(true)}
                                disabled={saving}
                            >
                                <i className="fa-solid fa-trash me-1"></i>
                                Eliminar álbum
                            </Button>
                        ) : (
                            <div />
                        )}

                        <div className="d-flex gap-2">
                            <Button
                                variant="secondary"
                                onClick={onClose}
                                disabled={saving}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-1" />
                                        {isEditMode ? "Guardando..." : "Creando..."}
                                    </>
                                ) : (
                                    isEditMode ? (
                                        <>
                                            <i className="fa-solid fa-check me-1"></i>
                                            Guardar cambios
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-plus me-1"></i>
                                            Crear álbum
                                        </>
                                    )
                                )}
                            </Button>
                        </div>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal de confirmación para eliminar */}
            <ConfirmationModal
                show={showConfirmDelete}
                title="Eliminar álbum"
                message="¿Estás seguro de que deseas eliminar este álbum? Las canciones no se eliminarán del catálogo general."
                confirmText="Eliminar álbum"
                cancelText="Cancelar"
                confirmVariant="danger"
                loading={deleting}
                onClose={() => setShowConfirmDelete(false)}
                onConfirm={handleDelete}
            />
        </>
    );
};

export default AlbumEditModal;
